/**
 * NovaThreadParticipant — Nova as a reply INSIDE a thread
 * Uses unified Nova block (orb + Nova + context pill + body + CTAs).
 */

import React from 'react';
import { NovaUnifiedCommunityReply } from './NovaUnified';
import { NOVA_VOICE, getCircuitBreakerSuggestions, getEvidenceSummary } from '../data/novaCommunityConfig';

const CHAPTER_THEMES = {
  fertility: { accent: '#D4537E', accentBg: '#FBEAF0' },
  ttc: { accent: '#D4537E', accentBg: '#FBEAF0' },
  pregnancy: { accent: '#D4537E', accentBg: '#FBEAF0' },
  motherhood: { accent: '#9B2942', accentBg: '#F5EAED' },
  postpartum: { accent: '#9B2942', accentBg: '#F5EAED' },
  menopause: { accent: '#D4537E', accentBg: '#FBEAF0' },
  cycle: { accent: '#D4537E', accentBg: '#FBEAF0' },
  'first-chapter': { accent: '#D4537E', accentBg: '#FBEAF0' },
};

function communityGuidePillFromVoiceTitle(title) {
  const t = String(title || '');
  if (/community guide/i.test(t)) return 'Community guide';
  if (/TTC guide/i.test(t)) return 'Community guide';
  if (/pregnancy guide/i.test(t)) return 'Community guide';
  if (/motherhood guide/i.test(t)) return 'Community guide';
  if (/First Chapter guide/i.test(t)) return 'Community guide';
  if (/menopause guide/i.test(t)) return 'Community guide';
  return 'Community guide';
}

export function NovaThreadParticipant({
  theme,
  chapter = 'fertility',
  spiral,
  originalPostText,
  onOpenNova,
  onLearnMore,
  feedSkin,
}) {
  const chapterKey = chapter === 'trying-to-conceive' ? 'ttc' : chapter;
  const voice = NOVA_VOICE[chapterKey] || NOVA_VOICE.fertility;
  const spiralResponses = voice.spiralResponses || NOVA_VOICE.fertility.spiralResponses;
  const responseFn = spiralResponses?.[spiral?.type];
  const t = theme || CHAPTER_THEMES[chapterKey] || CHAPTER_THEMES.fertility;

  if (spiral?.confidence === 'low') return null;

  const context = originalPostText?.length > 60
    ? `${originalPostText.slice(0, 57)}...`
    : (originalPostText || '');
  const message = responseFn ? responseFn(context) : '';

  const footerDefault = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginTop: 4, flexWrap: 'wrap' }}>
      <button
        type="button"
        onClick={onLearnMore}
        style={{
          background: 'none',
          border: 'none',
          padding: 0,
          fontSize: 11,
          fontWeight: 500,
          color: '#D4537E',
          cursor: 'pointer',
          textDecoration: 'none',
          fontFamily: 'inherit',
        }}
        onMouseEnter={(e) => { e.currentTarget.style.textDecoration = 'underline'; }}
        onMouseLeave={(e) => { e.currentTarget.style.textDecoration = 'none'; }}
      >
        {voice.redirectCTA}
      </button>
      <button
        type="button"
        onClick={onOpenNova}
        style={{
          background: 'none',
          border: 'none',
          padding: 0,
          fontSize: 11,
          color: 'rgba(60,30,45,0.5)',
          cursor: 'pointer',
          fontFamily: 'inherit',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.color = '#2d1a22';
          e.currentTarget.style.textDecoration = 'underline';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.color = 'rgba(60,30,45,0.5)';
          e.currentTarget.style.textDecoration = 'none';
        }}
      >
        Talk to Nova directly →
      </button>
    </div>
  );

  const pill = communityGuidePillFromVoiceTitle(voice.communityTitle);

  if (feedSkin === 'fertilityCommunity') {
    return (
      <div className="com-nova-reply">
        <div className="com-nova-reply__head">
          <div className="com-nova-av">N</div>
          <div style={{ minWidth: 0 }}>
            <span className="com-nova-name">Nova</span>
            <span className="com-nova-badge">{pill}</span>
          </div>
        </div>
        <p className="com-nova-body" style={{ marginBottom: spiral?.type === 'anxiety_amplification' ? 8 : 12 }}>{message}</p>

        {spiral?.type === 'grief_contagion' && (
          <div style={{ marginBottom: 8 }}>
            {getCircuitBreakerSuggestions(chapter).map((s, i) => (
              <div key={i} className="com-nova-suggest">
                <span>{s.emoji}</span>
                <span>{s.text}</span>
              </div>
            ))}
          </div>
        )}

        {spiral?.type === 'anxiety_amplification' && (
          <div className="com-nova-insight">
            <div className="com-nova-insight__title">What the evidence shows</div>
            <p className="com-nova-insight__body">{getEvidenceSummary(chapter, spiral.type)}</p>
          </div>
        )}

        <div className="com-nova-actions">
          <button type="button" className="com-nova-action" onClick={onLearnMore}>
            {voice.redirectCTA}
          </button>
          <button type="button" className="com-nova-action" onClick={onOpenNova}>
            Talk to Nova directly →
          </button>
        </div>
      </div>
    );
  }

  return (
    <NovaUnifiedCommunityReply
      contextPill={pill}
      timestamp="just now"
      footer={footerDefault}
    >
      <p style={{ fontSize: 12, color: '#4b1528', lineHeight: 1.5, marginBottom: 12 }}>{message}</p>

      {spiral?.type === 'grief_contagion' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 12 }}>
          {getCircuitBreakerSuggestions(chapter).map((s, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 12, color: '#2d1a22' }}>
              <span style={{ flexShrink: 0 }}>{s.emoji}</span>
              <span>{s.text}</span>
            </div>
          ))}
        </div>
      )}

      {spiral?.type === 'anxiety_amplification' && (
        <div
          style={{
            background: t.accentBg,
            borderRadius: 10,
            padding: '10px 12px',
            marginBottom: 12,
            border: '0.5px solid rgba(212,83,126,0.2)',
          }}
        >
          <p style={{ fontSize: 12, fontWeight: 700, color: '#993556', marginBottom: 4 }}>
            What the evidence shows
          </p>
          <p style={{ fontSize: 13, color: '#2d1a22', lineHeight: 1.5 }}>
            {getEvidenceSummary(chapter, spiral.type)}
          </p>
        </div>
      )}
    </NovaUnifiedCommunityReply>
  );
}

export default NovaThreadParticipant;
